import { Fragment, StrictMode, useLayoutEffect, useRef } from 'react';
import { Metrics } from './metrics';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
// prettier-ignore
// @ts-ignore untyped loader
import { name as packageName, version as packageVersion } from 'current-package-loader!';
import { getShouldStartWithCopilotOpen } from './copilot';
import { GridStyles, setupGridClasses } from './grid';
import { QUARTZ_APP_CONTAINER_CLASS } from 'quartz/grid';
import { clearMarks, clearMeasures, getEntriesByName, mark, measure } from './perf';
import { getSafeTracer, onTracingReady, SpanStatusCode, context, trace } from 'opentelemetry-fe';
import enviro from 'enviro';
import { na1 } from 'hubspot-url-utils/hublets';
import { QUARTZ_TRELLIS_MAIN_CONTENT_CLASS } from 'quartz/trellis-page-layout-styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function recordBootstrapSpan({
  appEntryTime,
  authFinishedTime,
  prepareAppFinishedTime,
  beforeRootRenderTime,
  afterRootRenderTime,
  authType,
  portalId,
  userId
}) {
  var _enviro$getHublet;
  const tracer = getSafeTracer('quartz');
  const attributes = {
    'quartz.app_name': packageName,
    'quartz.app_version': packageVersion,
    'deployment.environment': enviro.isProd() ? 'production' : 'qa',
    'hub.hublet': (_enviro$getHublet = enviro.getHublet()) !== null && _enviro$getHublet !== void 0 ? _enviro$getHublet : na1
  };
  if (authType != null) attributes['quartz.auth_type'] = authType;
  if (portalId != null) attributes['quartz.portal_id'] = portalId;
  if (userId != null) attributes['quartz.user_id'] = String(userId);
  const bootstrapSpan = tracer.startSpan('quartz.bootstrap', {
    startTime: appEntryTime,
    attributes
  });
  const bootstrapCtx = trace.setSpan(context.active(), bootstrapSpan);
  context.with(bootstrapCtx, () => {
    if (authFinishedTime > 0) {
      const authSpan = tracer.startSpan('quartz.auth', {
        startTime: appEntryTime
      });
      authSpan.setStatus({
        code: SpanStatusCode.OK
      });
      authSpan.end(authFinishedTime);
    }
    if (prepareAppFinishedTime > 0) {
      const prepareStart = authFinishedTime > 0 ? authFinishedTime : appEntryTime;
      const prepareSpan = tracer.startSpan('quartz.prepare_app', {
        startTime: prepareStart
      });
      prepareSpan.setStatus({
        code: SpanStatusCode.OK
      });
      prepareSpan.end(prepareAppFinishedTime);
    }
    if (beforeRootRenderTime > 0) {
      const renderSpan = tracer.startSpan('quartz.react_render', {
        startTime: beforeRootRenderTime
      });
      renderSpan.setStatus({
        code: SpanStatusCode.OK
      });
      renderSpan.end(afterRootRenderTime);
    }
  });
  if (prepareAppFinishedTime > 0 && beforeRootRenderTime > 0) {
    bootstrapSpan.addEvent('quartz.pre_render_setup', {
      duration_ms: beforeRootRenderTime - prepareAppFinishedTime
    }, prepareAppFinishedTime);
  }
  bootstrapSpan.setStatus({
    code: SpanStatusCode.OK
  });
  bootstrapSpan.end(afterRootRenderTime);
}
export function Root({
  children,
  isGraniteEnabled = false,
  isGraniteRemoteApp = false,
  portalId,
  userId,
  authType
}) {
  const portalIdRef = useRef(portalId);
  portalIdRef.current = portalId;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  useLayoutEffect(() => {
    var _getEntriesByName$0$s, _getEntriesByName$, _getEntriesByName$0$s2, _getEntriesByName$2, _getEntriesByName$0$s3, _getEntriesByName$3, _getEntriesByName$0$s4, _getEntriesByName$4, _getEntriesByName$0$s5, _getEntriesByName$5;
    mark('quartz.afterRootRender');

    // Capture timestamps before any clearMarks calls — marks are
    // cleared synchronously later in this same effect.
    const appEntryTime = (_getEntriesByName$0$s = (_getEntriesByName$ = getEntriesByName('quartz.appEntry', 'mark')[0]) === null || _getEntriesByName$ === void 0 ? void 0 : _getEntriesByName$.startTime) !== null && _getEntriesByName$0$s !== void 0 ? _getEntriesByName$0$s : 0;
    const authFinishedTime = (_getEntriesByName$0$s2 = (_getEntriesByName$2 = getEntriesByName('quartz.authFinished', 'mark')[0]) === null || _getEntriesByName$2 === void 0 ? void 0 : _getEntriesByName$2.startTime) !== null && _getEntriesByName$0$s2 !== void 0 ? _getEntriesByName$0$s2 : 0;
    const prepareAppFinishedTime = (_getEntriesByName$0$s3 = (_getEntriesByName$3 = getEntriesByName('quartz.prepareAppFinished', 'mark')[0]) === null || _getEntriesByName$3 === void 0 ? void 0 : _getEntriesByName$3.startTime) !== null && _getEntriesByName$0$s3 !== void 0 ? _getEntriesByName$0$s3 : 0;
    const beforeRootRenderTime = (_getEntriesByName$0$s4 = (_getEntriesByName$4 = getEntriesByName('quartz.beforeRootRender', 'mark')[0]) === null || _getEntriesByName$4 === void 0 ? void 0 : _getEntriesByName$4.startTime) !== null && _getEntriesByName$0$s4 !== void 0 ? _getEntriesByName$0$s4 : 0;
    const afterRootRenderTime = (_getEntriesByName$0$s5 = (_getEntriesByName$5 = getEntriesByName('quartz.afterRootRender', 'mark')[0]) === null || _getEntriesByName$5 === void 0 ? void 0 : _getEntriesByName$5.startTime) !== null && _getEntriesByName$0$s5 !== void 0 ? _getEntriesByName$0$s5 : 0;

    // @see https://product.hubteam.com/docs/fe-performance-reliability-handbook/tools/metrics.html#labelsdimensions
    const labels = {
      is_granite_enabled: String(isGraniteEnabled),
      is_granite_remote_app: String(isGraniteRemoteApp)
    };

    // `performance.mark()` doesn't return a performance `PerformanceMark` in some browsers, so we use `getEntriesByName`
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark#browser_compatibility
    getEntriesByName('quartz.appEntry', 'mark').forEach(entry => {
      Metrics.timer('appEntry', labels).update(entry.startTime);
    });
    getEntriesByName('quartz.authFinished', 'mark').forEach(entry => {
      Metrics.timer('authFinished', labels).update(entry.startTime);
    });
    clearMarks('quartz.authFinished');
    getEntriesByName('quartz.prepareAppFinished', 'mark').forEach(entry => {
      Metrics.timer('prepareAppFinished', labels).update(entry.startTime);
    });
    clearMarks('quartz.prepareAppFinished');
    getEntriesByName('quartz.beforeRootRender', 'mark').forEach(entry => {
      Metrics.timer('beforeRootRender', labels).update(entry.startTime);
    });
    measure('quartz.rootRender', 'quartz.beforeRootRender', 'quartz.afterRootRender', {
      metricTrackingName: 'rootRender',
      labels
    });
    clearMarks('quartz.afterRootRender');

    // The rough time it takes to get from our app entry to the handoff to the
    // app render
    measure('quartz.startupTime', 'quartz.appEntry', 'quartz.beforeRootRender', {
      metricTrackingName: 'appStartup',
      labels
    });
    clearMarks('quartz.appEntry');
    clearMarks('quartz.beforeRootRender');
    clearMeasures('quartz.rootRender');
    clearMeasures('quartz.startupTime');

    // Record OTel bootstrap span. appEntryTime > 0 guards against re-runs after
    // marks have been cleared (e.g. React strict-mode double invocation). Note:
    // QUARTZ_PERF_DEBUG disables clearMarks, so duplicate spans are expected in
    // debug mode.
    if (appEntryTime > 0) {
      onTracingReady(() => {
        recordBootstrapSpan({
          appEntryTime,
          authFinishedTime,
          prepareAppFinishedTime,
          beforeRootRenderTime,
          afterRootRenderTime,
          authType,
          portalId: portalIdRef.current,
          userId: userIdRef.current
        });
      });
    }
  }, [isGraniteEnabled, isGraniteRemoteApp, authType]);
  return children;
}
function getLegacyRootLocalStorageOverride() {
  try {
    const value = localStorage.getItem('LEGACY_ROOT_OVERRIDE');
    const parsedValue = value !== null ? JSON.parse(value) : null;
    return typeof parsedValue === 'boolean' ? parsedValue : null;
  } catch (_unused) {
    return null;
  }
}
export function renderRoot(reactElement, {
  strictMode = false,
  legacyRoot = true,
  copilotLayoutType,
  isCopilotDisabled = false,
  container: containerFromOptions,
  hasTrellisModernizedLayout = false,
  isGraniteEnabled = false,
  isGraniteRemoteApp = false,
  portalId,
  userId,
  authType
} = {}) {
  let container = containerFromOptions !== null && containerFromOptions !== void 0 ? containerFromOptions : null;
  if (!container) {
    if (hasTrellisModernizedLayout) {
      container = document.querySelector(`.${QUARTZ_TRELLIS_MAIN_CONTENT_CLASS}`);
      if (!container) {
        Metrics.counter('quartz.trellisLayout.noContainer').increment();
      }
    } else if (copilotLayoutType && copilotLayoutType !== 'panel') {
      container = document.querySelector(`.${QUARTZ_APP_CONTAINER_CLASS}`);
      if (!container) {
        Metrics.counter('quartz.copilotLayout.noContainer').increment();
        container = document.querySelector('.page');
      }
    } else {
      container = document.querySelector('.page');
    }
  }
  if (!container) {
    throw new Error('Invariant: Could not find container element');
  }
  if (copilotLayoutType) {
    setupGridClasses(copilotLayoutType);
  }
  const StrictModeIfEnabled = strictMode ? StrictMode : Fragment;
  const children = /*#__PURE__*/_jsx(Root, {
    isGraniteEnabled: isGraniteEnabled,
    isGraniteRemoteApp: isGraniteRemoteApp,
    portalId: portalId,
    userId: userId,
    authType: authType,
    children: /*#__PURE__*/_jsxs(StrictModeIfEnabled, {
      children: [copilotLayoutType && /*#__PURE__*/_jsx(GridStyles, {
        shouldSidebarStartOpen: !isCopilotDisabled && getShouldStartWithCopilotOpen()
      }), reactElement]
    })
  });
  mark('quartz.beforeRootRender');
  const legacyRootOverride = getLegacyRootLocalStorageOverride();
  if (legacyRootOverride !== null) {
    legacyRoot = legacyRootOverride;
  }

  // ReactDOM.render is removed in React 19, so the legacy path only works on
  // React 18. We reach the legacy API through a locally-declared shape rather
  // than react-dom's own types so this source type-checks against both React 18
  // and 19 (the react-dom@19 module type no longer declares these members), and
  // fall back to createRoot when they're unavailable at runtime.
  const legacyReactDom = ReactDOM;
  const supportsLegacyRoot = typeof legacyReactDom.render === 'function';
  if (!legacyRoot || !supportsLegacyRoot) {
    const root = createRoot(container, {
      identifierPrefix: packageName
    });
    root.render(children);
    return root;
  } else {
    legacyReactDom.render(children, container);
    return {
      unmount() {
        legacyReactDom.unmountComponentAtNode(container);
      }
    };
  }
}